import type { FC } from 'react'

import { LoaderWrapper, Table } from '@skytech/ui'
import { getQueryFilter, getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/App.context'
import { useSortParams } from '@/hooks/searchParams'

import { useTranslation } from '../i18n/hooks'
import ListLoader from '../util/ListLoader'
import NoResults from '../util/NoResults'

import { INITIAL_PER_PAGE_LIMIT, PER_PAGE_LIMIT } from './consts'
import { getIsOrderOpen } from './helpers'
import { queries } from './queries'
import type { OrderStatusCount, PortingOrder } from './resources'
import subscriptionOrderResources from './resources'
import { SubscriptionOrderFilters } from './SubscriptionOrderFilters'
import { SubscriptionOrderListItem } from './SubscriptionOrdersTable/SubscriptionOrderListItem'
import { SubscriptionOrdersTHead } from './SubscriptionOrdersTable/SubscriptionOrdersTHead'

export const SubscriptionOrderPortingList: FC<{
  textSearch: string
  status: string
  querySearch: string
  statusCounter: OrderStatusCount
  setTextSearch: (val: string) => void
}> = ({ textSearch, statusCounter, status, querySearch, setTextSearch }) => {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()
  const [{ querySort, querySortBy }] = useSortParams<keyof PortingOrder>()

  const portingQuery = useInfiniteQuery({
    queryKey: queries.portingSubscriptionOrders({
      keyword: querySearch,
      status,
      sortBy: querySortBy,
      sort: querySort,
    }),
    queryFn: ({ pageParam = 1 }) => {
      const limit = pageParam === 1 ? INITIAL_PER_PAGE_LIMIT : PER_PAGE_LIMIT
      const sort = !querySort || querySort === 'off' ? undefined : querySort
      return subscriptionOrderResources.getPortingList({
        offset: pageParam,
        limit,
        keyword: querySearch,
        status,
        sortBy: querySortBy ?? undefined,
        sort: sort,
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.length, 0)
      if (lastPage.length < (allPages.length === 1 ? INITIAL_PER_PAGE_LIMIT : PER_PAGE_LIMIT)) {
        return undefined
      }
      return totalFetched + 1
    },
  })
  const orders = portingQuery.data?.pages.flat() || []
  const isLoaderVisible = Boolean(portingQuery.hasNextPage && !portingQuery.isFetchingNextPage)

  function subOrderStringMatcher(order: PortingOrder) {
    return [
      order.id,
      order.customer,
      order.user,
      order.code,
      translate(`STATUS_SUBSCRIPTION_ORDER/${order.subStatus}`),
    ]
  }

  const filteredOrders = orders.filter(order => {
    const statusFilter =
      status === 'all' ||
      (status === 'completed' && order.subStatus === 'done') || // Handle 'completed' as 'done'
      status === order.subStatus ||
      (status === 'open' && getIsOrderOpen(order.subStatus))
    const searchFilter = getQueryFilter(textSearch, subOrderStringMatcher)(order)
    return statusFilter && searchFilter
  })

  const { data: totalCount, isLoading: isTotalCountLoading } = useQuery(
    queries.portingSubscriptionOrdersTotalCount({ keyword: querySearch, status }),
    () =>
      subscriptionOrderResources.getTotalOrders({
        keyword: querySearch,
        status,
        orderType: 'onboarding',
      })
  )

  return (
    <>
      <SubscriptionOrderFilters
        statusCounter={statusCounter}
        totalCount={totalCount}
        isTotalCountLoading={isTotalCountLoading}
        status={status}
        textSearch={textSearch}
        setTextSearch={(val: string) => setTextSearch(val)}
      />
      <LoaderWrapper
        isLoading={portingQuery.isInitialLoading}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      >
        <div className="max-w-full">
          <Table rowHeight="tall">
            {orders.length > 0 && <SubscriptionOrdersTHead />}

            <tbody>
              {filteredOrders.map(order => (
                <SubscriptionOrderListItem key={order.id} {...order} textSearch={textSearch} />
              ))}
            </tbody>
          </Table>

          {orders.length === 0 && <NoResults />}

          <ListLoader
            conditionSelector={() => isLoaderVisible}
            action={portingQuery.fetchNextPage}
          />
        </div>
      </LoaderWrapper>
    </>
  )
}
